import { useState } from 'react';
import { supabase } from '@/lib/supabase';

interface Props {
  staffId: string;
  staffName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function todayJst() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export default function EmploymentTermModal({ staffId, staffName, onClose, onSuccess }: Props) {
  const [employmentType, setEmploymentType] = useState<'hourly' | 'monthly'>('hourly');
  const [amount, setAmount] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayJst());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = Number(amount);
    if (!Number.isInteger(value) || value <= 0) {
      setError('金額を正しく入力してください');
      return;
    }
    setSaving(true); setError(null);
    const { data, error: rpcError } = await supabase.rpc('admin_set_staff_employment_term', {
      p_staff_id: staffId,
      p_employment_type: employmentType,
      p_hourly_wage_yen: employmentType === 'hourly' ? value : null,
      p_monthly_salary_yen: employmentType === 'monthly' ? value : null,
      p_effective_from: effectiveFrom,
    });
    const result = data as { success?: boolean; error?: string } | null;
    if (rpcError || !result?.success) {
      setError('雇用条件を保存できませんでした');
      setSaving(false);
      return;
    }
    onSuccess();
  };

  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-background-50 rounded-xl border border-background-200 w-full max-w-md mx-4 p-5">
      <div className="flex items-center justify-between mb-5">
        <div><h3 className="font-semibold text-foreground-900">給与条件を変更</h3><p className="text-xs text-foreground-600 mt-1">{staffName}</p></div>
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100"><i className="ri-close-line" /></button>
      </div>
      {error && <div className="mb-4 bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">{error}</div>}
      <form onSubmit={submit} className="space-y-4">
        <div><label className="block text-sm font-medium mb-1">給与形態</label><select value={employmentType} onChange={(e) => setEmploymentType(e.target.value as 'hourly' | 'monthly')} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50"><option value="hourly">時給制</option><option value="monthly">月給制</option></select></div>
        <div><label className="block text-sm font-medium mb-1">{employmentType === 'hourly' ? '基本時給（円）' : '基本月給（円）'}</label><input type="number" min="1" step="1" required value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200" /></div>
        <div><label className="block text-sm font-medium mb-1">適用開始日</label><input type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200" /><p className="text-xs text-foreground-500 mt-1">以前の給与条件は前日で終了し、履歴として残ります。</p></div>
        <div className="flex gap-3 pt-2"><button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-background-100 text-foreground-700">キャンセル</button><button disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold disabled:opacity-60">{saving ? '保存中…' : '保存'}</button></div>
      </form>
    </div>
  </div>;
}
